import { ChartData } from 'chart.js';
import { useEffect, useState } from 'react';

import {
  GpteLLMTokensUsageRequest,
  GpteTokenConsumptionStatsRequest,
  GpteUsageStatsRequest,
} from '../../../../../admin-center/gen/h2ogpte/h2ogpte_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import {
  buildUserContext,
  FlatRawChartData,
  formatDateLocal,
  NestedRawChartData,
  transformFlatDataForCharts,
  transformFlatDataForPieChart,
  transformNestedDataForCategoricalCharts,
  transformNestedDataForCharts,
} from '../../../common/utils';
import {
  descriptionsColorMap,
  gpteDailyTokenUsageColorMap,
  gpteDailyTokenUsageLabelsMap,
  keyNameMap,
  llmColorMap,
  mapGPTeKeys,
} from '../H2OGPTe.utils';

export const useGPTeTokenStats = (startDate: Date, endDate: Date) => {
  const service = useAdminCenterService();
  const [tokenStatsLoading, setTokenStatsLoading] = useState<boolean>(true);
  const [tokenStatsError, setTokenStatsError] = useState<string | null>(null);

  const [inputTokens, setInputTokens] = useState<number>(0);
  const [outputTokens, setOutputTokens] = useState<number>(0);
  const [tokensPerMessage, setTokensPerMessage] = useState<number>(0);
  const [mostUsedLLM, setMostUsedLLM] = useState<string>('');
  const [llmTokenUsage, setLLMTokenUsage] = useState<number>(0);
  const [llmUsageCount, setLLMUsageCount] = useState<number>(0);

  const user = useUser();

  useEffect(() => {
    const fetchGPTeTokenStats = async () => {
      setTokenStatsLoading(true);
      try {
        const userContext = await buildUserContext(user);
        const request: GpteTokenConsumptionStatsRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
          userContext: userContext,
        };

        const response = await service.getTokenConsumptionStats(request);

        setInputTokens(response.totalInputTokens || 0);
        setOutputTokens(response.totalOutputTokens || 0);
        setTokensPerMessage(response.averageTokensPerMessage || 0);
        setMostUsedLLM(response.mostUsedLlm || '');
        setLLMTokenUsage(response.totalTokensOfMostUsedLlm || 0);
        setLLMUsageCount(response.mostUsedLlmCount || 0);
      } catch (error) {
        setTokenStatsError('Error fetching login events');
      } finally {
        setTokenStatsLoading(false);
      }
    };

    fetchGPTeTokenStats();
  }, [startDate, endDate, service, user]);

  return {
    inputTokens,
    outputTokens,
    tokensPerMessage,
    mostUsedLLM,
    llmTokenUsage,
    llmUsageCount,
    tokenStatsLoading,
    tokenStatsError,
  };
};

export const useDailyTokenUsageData = (startDate: Date, endDate: Date) => {
  const service = useAdminCenterService();
  const [dailyTokenUsageLoading, setDailyTokenUsageLoading] = useState<boolean>(true);
  const [dailyTokenUsageError, setDailyTokenUsageError] = useState<string | null>(null);

  const [dailyTokenUsageChartData, setDailyTokenUsageChartData] = useState<ChartData<'line'>>();

  useEffect(() => {
    const fetchDailyTokenUsageData = async () => {
      setDailyTokenUsageLoading(true);
      try {
        const request: GpteTokenConsumptionStatsRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
        };

        const response = await service.getDailyTokenUsage(request);
        const transformedData = transformNestedDataForCharts(
          response.data as NestedRawChartData,
          gpteDailyTokenUsageLabelsMap,
          gpteDailyTokenUsageColorMap
        );
        setDailyTokenUsageChartData(transformedData as ChartData<'line'>);
      } catch (error) {
        setDailyTokenUsageError('Error fetching login events');
      } finally {
        setDailyTokenUsageLoading(false);
      }
    };

    fetchDailyTokenUsageData();
  }, [startDate, endDate, service]);

  return {
    dailyTokenUsageChartData,
    dailyTokenUsageLoading,
    dailyTokenUsageError,
  };
};

export const useLLMTokenUsageData = (startDate: Date, endDate: Date) => {
  const service = useAdminCenterService();
  const [llmTokenUsageLoading, setLLMTokenUsageLoading] = useState<boolean>(true);
  const [llmTokenUsageError, setLLMTokenUsageError] = useState<string | null>(null);

  const [llmTokenUsageChartData, setLLMTokenUsageChartData] = useState<ChartData<'bar'>>();

  useEffect(() => {
    const fetchLLMTokenUsageData = async () => {
      setLLMTokenUsageLoading(true);
      try {
        const request: GpteLLMTokensUsageRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
        };

        const response = await service.getLLMTokenUsage(request);
        const transformedData = transformNestedDataForCategoricalCharts(
          getTopLLMs(response.data) as NestedRawChartData,
          gpteDailyTokenUsageLabelsMap,
          gpteDailyTokenUsageColorMap,
          keyNameMap
        );
        setLLMTokenUsageChartData(transformedData as ChartData<'bar'>);
      } catch (error) {
        setLLMTokenUsageError('Error fetching login events');
      } finally {
        setLLMTokenUsageLoading(false);
      }
    };

    fetchLLMTokenUsageData();
  }, [startDate, endDate, service]);

  return {
    llmTokenUsageChartData,
    llmTokenUsageLoading,
    llmTokenUsageError,
  };
};

const getTopLLMs = (
  data: Record<string, { inputTokens?: number; outputTokens?: number }> = {},
  topCount = 5
): Record<string, { inputTokens: number; outputTokens: number }> => {
  const llmEntries = Object.entries(data)
    .filter(([key]) => key.trim() !== '')
    .map(([key, tokens]) => ({
      key,
      tokens: {
        inputTokens: tokens.inputTokens ?? 0,
        outputTokens: tokens.outputTokens ?? 0,
      },
      total: (tokens.inputTokens ?? 0) + (tokens.outputTokens ?? 0),
    }))
    .sort((a, b) => b.total - a.total);

  const topEntries = llmEntries.slice(0, topCount);
  const otherEntries = llmEntries.slice(topCount);

  const result: Record<string, { inputTokens: number; outputTokens: number }> = {};

  topEntries.forEach(({ key, tokens }) => {
    result[key] = tokens;
  });

  if (otherEntries.length > 0) {
    result['Other'] = otherEntries.reduce(
      (acc, { tokens }) => ({
        inputTokens: acc.inputTokens + tokens.inputTokens,
        outputTokens: acc.outputTokens + tokens.outputTokens,
      }),
      { inputTokens: 0, outputTokens: 0 }
    );
  }

  return result;
};

type ChartsState = {
  apiCalledData: ChartData;
  chatData: ChartData;
  collectionsData: ChartData;
  documentsData: { [key: string]: number };
  jobData: { [key: string]: number };
  llmDescriptionData: ChartData;
  llmUsageData: ChartData;
  rpcCalledData: ChartData;
};

export const useGPTeStats = (startDate: Date, endDate: Date) => {
  const service = useAdminCenterService();
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [chartsData, setChartsData] = useState<ChartsState>({
    apiCalledData: { labels: [], datasets: [] },
    chatData: { labels: [], datasets: [] },
    collectionsData: { labels: [], datasets: [] },
    documentsData: {},
    jobData: {},
    llmDescriptionData: { labels: [], datasets: [] },
    llmUsageData: { labels: [], datasets: [] },
    rpcCalledData: { labels: [], datasets: [] },
  });

  useEffect(() => {
    const fetchGPTeStats = async () => {
      setStatsLoading(true);
      try {
        const request: GpteUsageStatsRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
        };

        const response = await service.getGpteUsageStats(request);

        setChartsData({
          llmDescriptionData: transformFlatDataForPieChart(
            response.llmDescription as FlatRawChartData,
            descriptionsColorMap,
            keyNameMap,
            '#fde047'
          ),
          llmUsageData: transformFlatDataForPieChart(
            response.llmUsage as FlatRawChartData,
            llmColorMap,
            keyNameMap,
            '#fde047'
          ),
          apiCalledData: transformFlatDataForCharts(
            mapGPTeKeys(response.apiCalled) as FlatRawChartData,
            'Count',
            '#fde047'
          ),
          rpcCalledData: transformFlatDataForCharts(
            mapGPTeKeys(response.rpcCalled) as FlatRawChartData,
            'Count',
            '#fde047'
          ),
          chatData: transformFlatDataForCharts(
            mapGPTeKeys(response.chatActions) as FlatRawChartData,
            'Count',
            '#fde047'
          ),
          collectionsData: transformFlatDataForCharts(
            mapGPTeKeys(response.collectionActions) as FlatRawChartData,
            'Count',
            '#fde047'
          ),
          documentsData: Object.fromEntries(
            Object.entries(mapGPTeKeys(response.documentActions) || {}).map(([key, value]) => [key, Number(value)])
          ),
          jobData: Object.fromEntries(
            Object.entries(mapGPTeKeys(response.jobActions) || {}).map(([key, value]) => [key, Number(value)])
          ),
        });
      } catch (error) {
        setStatsError('Error fetching login events');
      } finally {
        setStatsLoading(false);
      }
    };

    fetchGPTeStats();
  }, [startDate, endDate, service]);

  return { chartsData, statsLoading, statsError };
};
